import { FC, SyntheticEvent, useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { useFlags } from '@mint-lib/routing-context';

import { Autocomplete, TextField } from '@myn/mui';

import { ColumnOptionItem } from '../../pages/Builder/types.js';
import {
  filterSoftDeletedOptions,
  getOptionDisabled,
  getOptionLabel,
  getSelectedOption,
  isOptionEqualToValue,
} from '../../utils/autocomplete.js';

type Props = {
  value: string | null;
  options: ColumnOptionItem[];
  onChange: (e: SyntheticEvent, value: ColumnOptionItem | null) => void;
  onClose: () => void;
  format: (value: string | number) =>
    | {
        formatted: string;
        mapping: number[];
      }
    | undefined;
};

const FormattedAutocomplete: FC<Props> = ({
  value,
  options,
  onChange,
  onClose,
  format,
}) => {
  const { t } = useTranslation('@myn/planning');
  const ff = useFlags('@mint/arm', ['mdmColumnsForBuyingPlan']);

  const formattedOptions = options.map((option) => {
    return {
      ...option,
      displayName: format(option.value)?.formatted || option.value,
    };
  });

  const selectedOption = getSelectedOption(value, formattedOptions);

  const [inputValue, setInputValue] = useState<string>(
    selectedOption.displayName,
  );

  const handleInputValueChange = (e: SyntheticEvent, newInputValue: string) => {
    if (!e) {
      return;
    }

    setInputValue(newInputValue);
  };

  return (
    <Autocomplete
      size="medium"
      options={formattedOptions}
      renderInput={(params) => (
        <TextField {...params} autoFocus placeholder={t('Search')} />
      )}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      value={selectedOption}
      onChange={onChange}
      openOnFocus
      onClose={onClose}
      disableClearable={!inputValue}
      inputValue={inputValue}
      onInputChange={handleInputValueChange}
      filterOptions={filterSoftDeletedOptions}
      getOptionDisabled={
        ff.mdmColumnsForBuyingPlan.enabled ? getOptionDisabled : undefined
      }
    />
  );
};

export default FormattedAutocomplete;
